import { useClient } from '@composables/use-clients';
import {
  GetRequestArgsType,
  PostRequestArgsType,
  PutRequestArgsType,
  DeleteRequestArgsType,
} from '@api/v1/types';
import { useTenantStore } from '@stores/tenant-store';
import { urls } from '@api/urls';
import { withTryCatch } from '@api/v1/error-handlers';

export type GetTeamArgType = GetRequestArgsType & {
  teamCode: string;
};

export type PostNewTeamPayloadType = {
  name: string;
  description: string;
  skillNames: string[];
};

export type PutTeamPayloadType = {
  name: string;
  description: string;
};

export type PutTeamArgsType = PutRequestArgsType<PutTeamPayloadType> & {
  teamCode: string;
};

export type PutTeamVisibilityPayloadType = {
  isVisible: boolean;
};

export type PutTeamVisibilityArgsType = PutRequestArgsType<PutTeamVisibilityPayloadType> & {
  teamCode: string;
};

export type DeleteTeamArgsType = DeleteRequestArgsType & {
  teamCode: string;
};

export const apiFetchTeams = async ({
  domain = useTenantStore().domain,
  query = {},
  errorConfig = {},
  callbacks = {},
}: GetRequestArgsType = {}) => {
  const impl = async () => {
    const { client } = useClient('auth');
    const res = await client.get({
      url: urls.v1.teams({ domain }),
      query,
    });
    return res;
  };

  return withTryCatch({ impl, errorConfig, callbacks });
};

export const apiFetchTeam = async ({
  domain = useTenantStore().domain,
  teamCode,
  errorConfig = {},
  callbacks = {},
}: GetTeamArgType) => {
  const impl = async () => {
    const { client } = useClient('auth');
    const res = await client.get({
      url: urls.v1.teams({ domain, teamCode }),
    });
    return res;
  };

  return withTryCatch({ impl, errorConfig, callbacks });
};

export const apiCreateTeam = async ({
  domain = useTenantStore().domain,
  payload,
  errorConfig = {},
  callbacks = {},
}: PostRequestArgsType<PostNewTeamPayloadType>) => {
  const impl = async () => {
    const { client } = useClient('auth');
    const res = await client.post({
      url: urls.v1.newTeam({ domain }),
      payload,
    });
    return res;
  };

  return withTryCatch({ impl, errorConfig, callbacks });
};

export const apiUpdateTeam = async ({
  domain = useTenantStore().domain,
  teamCode,
  payload,
  errorConfig = {},
  callbacks = {},
}: PutTeamArgsType) => {
  const impl = async () => {
    const { client } = useClient('auth');
    const res = await client.put({
      url: urls.v1.teams({ domain, teamCode }),
      payload,
    });
    return res;
  };

  return withTryCatch({ impl, errorConfig, callbacks });
};

export const apiUpdateTeamVisibility = async ({
  domain = useTenantStore().domain,
  teamCode,
  payload,
  errorConfig = {},
  callbacks = {},
}: PutTeamVisibilityArgsType) => {
  const impl = async () => {
    const { client } = useClient('auth');
    const res = await client.put({
      url: urls.v1.teamVisibility({ domain, teamCode }),
      payload,
    });
    return res;
  };

  return withTryCatch({ impl, errorConfig, callbacks });
};

export const apiDeleteTeam = async ({
  domain = useTenantStore().domain,
  teamCode,
  errorConfig = {},
  callbacks = {},
}: DeleteTeamArgsType) => {
  const impl = async () => {
    const { client } = useClient('auth');
    const res = await client.delete({
      url: urls.v1.teams({ domain, teamCode }),
    });
    return res;
  };

  return withTryCatch({ impl, errorConfig, callbacks });
};
